import { useEffect } from "react";
import { useSnapshot } from "valtio";
import { analyticsStore, fetchGeography } from "../stores/analytics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BarChart, PieChart } from "../components/charts";
import { AlertCircle, Globe } from "lucide-react";
import { formatNumber } from "@/lib/utils";

export default function AnalyticsGeography() {
  const { geography, timeRange } = useSnapshot(analyticsStore);
  const { data, loading, error } = geography;

  useEffect(() => {
    fetchGeography();
  }, [timeRange]);

  // Transform data for charts
  const countryData =
    data?.by_country.slice(0, 8).map((c) => ({
      name: c.country,
      value: c.count,
    })) ?? [];

  const regionData =
    data?.by_region.slice(0, 5).map((r) => ({
      name: r.region,
      value: r.count,
    })) ?? [];

  const totalUsers = data?.by_country.reduce((sum, c) => sum + c.count, 0) ?? 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Card */}
      <Card className="mb-6">
        <CardContent className="py-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                Total Users Across
              </p>
              <p className="text-2xl font-bold">
                {data?.by_country.length ?? 0} Countries
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Grid */}
      <div className="grid gap-6 lg:grid-cols-2 mb-6">
        {/* Countries Chart */}
        {loading ? (
          <Skeleton className="h-[350px]" />
        ) : (
          <BarChart
            data={countryData}
            title="Top Countries"
            dataKey="value"
            xAxisKey="name"
            color="#667eea"
            height={300}
          />
        )}

        {/* Regions Pie */}
        {loading ? (
          <Skeleton className="h-[350px]" />
        ) : (
          <PieChart data={regionData} title="Top Regions" height={300} />
        )}
      </div>

      {/* Countries Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Countries</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Country</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead className="text-right">% of Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.by_country.map((country) => {
                  const percentage =
                    totalUsers > 0
                      ? ((country.count / totalUsers) * 100).toFixed(1)
                      : "0";
                  return (
                    <TableRow key={country.country}>
                      <TableCell className="font-medium">
                        {country.country}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(country.count)}
                      </TableCell>
                      <TableCell className="text-right">
                        {percentage}%
                      </TableCell>
                    </TableRow>
                  );
                }) ?? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center text-muted-foreground"
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
